import {
    ACCESS_TOKEN_NAME,
    IMPERSONATOR_ACCESS_TOKEN_NAME,
    IMPERSONATOR_REFRESH_TOKEN_NAME,
    REFRESH_TOKEN_NAME,
} from 'shared/lib/constants';

import Token from 'shared/api/model/Token';
import AuthTokens from 'shared/api/model/AuthTokens';

import {ThunkDispatch} from 'redux-thunk';

import {AnyAction} from 'redux';

import {isBrowser} from '../lib/evironment';
import {currentDatePlusHours} from '../lib/datetime';
import {TypedDispatch} from '../model/types';
import {fetchMe} from '../../entities/user/model/thunks/fetchMe';
import {logoutAction} from '../../entities/user/model/authSlice';
import {cacheCleared} from './cacheEvents';
import createHttpRequest from './http';
import {ApiUrls} from '../config/model/urls';

export const setTokens = (tokens: AuthTokens) => {
    if (isBrowser()) {
        setToken(ACCESS_TOKEN_NAME, tokens.token, currentDatePlusHours(1));
        setToken(REFRESH_TOKEN_NAME, tokens.refresh_token, currentDatePlusHours(30*24));
    }
};

export const backupImpersonatorTokens = () => {
    if (isBrowser()) {
        copyToken(ACCESS_TOKEN_NAME, IMPERSONATOR_ACCESS_TOKEN_NAME);
        copyToken(REFRESH_TOKEN_NAME, IMPERSONATOR_REFRESH_TOKEN_NAME);
    }
};

export const restoreImpersonatorTokens = () => {
    if (isBrowser()) {
        copyToken(IMPERSONATOR_ACCESS_TOKEN_NAME, ACCESS_TOKEN_NAME);
        copyToken(IMPERSONATOR_REFRESH_TOKEN_NAME, REFRESH_TOKEN_NAME);

        localStorage.removeItem(IMPERSONATOR_ACCESS_TOKEN_NAME);
        localStorage.removeItem(IMPERSONATOR_REFRESH_TOKEN_NAME);
    }
};

const copyToken = (source: string, target: string):void => {
    const sourceToken = getTokenData(source);
    if (sourceToken?.value && sourceToken?.expires) {
        setToken(
            target,
            sourceToken?.value,
            sourceToken?.expires,
        );
    }
};

export const isImpersonator = ():boolean => {
    return getToken(IMPERSONATOR_REFRESH_TOKEN_NAME) !== null;
};

// Окно возврата из имперсонации: пока флаг поднят, 401 на /users/me не должен уходить
// в refresh — ошибка обязана долететь до catch в logout, чтобы сработал обычный выход.
// Вне этого окна имперсонированная сессия обновляется штатно (см. interceptor в utils/http.ts).
let restoringImpersonator = false;

export const isRestoringImpersonator = ():boolean => restoringImpersonator;

export const clearTokens = () => {
    if (isBrowser()) {
        localStorage.removeItem(ACCESS_TOKEN_NAME);
        localStorage.removeItem(REFRESH_TOKEN_NAME);
    }
};

export const clearAccessTokens = () => {
    if (isBrowser()) {
        localStorage.removeItem(ACCESS_TOKEN_NAME);
    }
};

export async function clearCachedData(dispatch: ThunkDispatch<unknown, unknown, AnyAction>) {
    dispatch(cacheCleared());
}

export const logout = async(dispatch: TypedDispatch) => {
    clearTokens();

    if (isImpersonator()) {
        restoreImpersonatorTokens();
        await new Promise(resolve => setTimeout(resolve, 50));
        restoringImpersonator = true;
        try {
            await dispatch(fetchMe()).unwrap();
        } catch {
            clearTokens();
            dispatch(logoutAction());
        } finally {
            restoringImpersonator = false;
        }
        localStorage.removeItem(IMPERSONATOR_ACCESS_TOKEN_NAME);
        localStorage.removeItem(IMPERSONATOR_REFRESH_TOKEN_NAME);
    } else {
        clearTokens();
        dispatch(logoutAction());
    }

    await clearCachedData(dispatch);
};

export const getAccessToken = (): string | null => {
    return getToken(ACCESS_TOKEN_NAME);
};

export const getRefreshToken = (): string | null => {
    return getToken(REFRESH_TOKEN_NAME);
};

const getToken = (tokenName: string): string | null => {
    const token = getTokenData(tokenName);
    if (!token) {
        return null;
    }
    if (new Date() >= new Date(token.expires)) {
        return null;
    }

    return token.value;
};

const getTokenData = (tokenName: string): Token | null => {
    const tokenData: string | null = isBrowser()
        ? localStorage.getItem(tokenName)
        : null;

    if (!tokenData) {
        return null;
    }
    return JSON.parse(tokenData);
};

export const setToken = (tokenName: string, value: string, expires: string|Date) => {
    const token: Token = {value, expires};
    if (isBrowser()) {
        localStorage.setItem(tokenName, JSON.stringify(token));
    }
};
export const refreshTokens = async():Promise<void> => {
    const refreshToken = getRefreshToken();
    if (!refreshToken) {
        throw new Error('Отсутствует RefreshToken');
    }
    const response = await createHttpRequest({
        method: 'POST',
        path: ApiUrls.TOKEN_REFRESH,
        data: {refresh_token: refreshToken},
        isAnonymous: true,
    });
    setTokens(response.data);
};